// ============================================================================
// SCHEMA OVERRIDES — TENANT-AWARE PRESET LOADER
// ============================================================================
// The active schema overrides are resolved from VITE_THEME env var:
//   VITE_THEME=dumax       → src/config/schemas/dumax-schema.json
//   VITE_THEME=central-gps → src/config/schemas/central-gps-schema.json
//
// Fallback chain: {tenant}-schema.json → default-schema.json → empty preset
//
// Usage:
//   import { schemaConfig, isFieldVisible, isFieldRequired, getFieldConfig } from "./config/schemaConfig";
// ============================================================================

import type { SchemaOverridesPreset, FieldConfig } from "./schemas/types";

// ---------------------------------------------------------------------------
// Load all schema JSON files eagerly at build time
// ---------------------------------------------------------------------------
const schemaModules = import.meta.glob<{ default: SchemaOverridesPreset }>(
  "./schemas/*.json",
  { eager: true },
);

// ---------------------------------------------------------------------------
// Resolve active schema preset: {VITE_THEME}-schema.json → default-schema.json
// ---------------------------------------------------------------------------
const tenant = import.meta.env.VITE_THEME as string | undefined;

function resolveModule(key: string): SchemaOverridesPreset | undefined {
  const mod = schemaModules[key];
  if (!mod) return undefined;
  return (mod.default ?? mod) as unknown as SchemaOverridesPreset;
}

const EMPTY_PRESET: SchemaOverridesPreset = { id: "empty", entities: {} };

const activeSchemaPreset: SchemaOverridesPreset =
  (tenant ? resolveModule(`./schemas/${tenant}-schema.json`) : undefined) ??
  resolveModule("./schemas/default-schema.json") ??
  EMPTY_PRESET;

// ---------------------------------------------------------------------------
// Exported config object
// ---------------------------------------------------------------------------
export const schemaConfig: SchemaOverridesPreset = activeSchemaPreset;

// ---------------------------------------------------------------------------
// Default field config when no override data exists
// ---------------------------------------------------------------------------
const DEFAULT_FIELD: FieldConfig = { visible: true, required: false };

// ---------------------------------------------------------------------------
// Helper: get full field config for a specific entity + endpoint + field
// ---------------------------------------------------------------------------
export function getFieldConfig(
  entity: string,
  endpoint: string,
  field: string,
): FieldConfig {
  return activeSchemaPreset.entities[entity]?.[endpoint]?.[field] ?? DEFAULT_FIELD;
}

// ---------------------------------------------------------------------------
// Helper: is a field visible (not removed) in the CRUD form?
// ---------------------------------------------------------------------------
export function isFieldVisible(
  entity: string,
  endpoint: string,
  field: string,
): boolean {
  return getFieldConfig(entity, endpoint, field).visible;
}

// ---------------------------------------------------------------------------
// Helper: is a field required for form validation?
// ---------------------------------------------------------------------------
export function isFieldRequired(
  entity: string,
  endpoint: string,
  field: string,
): boolean {
  const cfg = getFieldConfig(entity, endpoint, field);
  return cfg.visible && cfg.required;
}

// ---------------------------------------------------------------------------
// Helper: get all field configs for an entity + endpoint
// ---------------------------------------------------------------------------
export function getEndpointFields(
  entity: string,
  endpoint: string,
): Record<string, FieldConfig> {
  return activeSchemaPreset.entities[entity]?.[endpoint] ?? {};
}

// ---------------------------------------------------------------------------
// Helper: get all visible field names for an entity + endpoint
// ---------------------------------------------------------------------------
export function getVisibleFields(
  entity: string,
  endpoint: string,
): string[] {
  const fields = getEndpointFields(entity, endpoint);
  return Object.entries(fields)
    .filter(([, cfg]) => cfg.visible)
    .map(([name]) => name);
}

export default schemaConfig;
